import { LicensesAction, LicensesActionTypes } from './licenses.actions';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { License, LicensePackage } from '@account-users/account/models/License';
import { AccountLicense } from '@account-users/account/models/AccountLicense';
import { AccountState } from '@account-users/account/models';

export const LICENSES_FEATURE_KEY = 'licenses';

export interface LicensesState extends EntityState<License> {
  accountLicenses: AccountLicense[];
  isLoadingLicenses: boolean;
  licensePackages: LicensePackage[];
  isLoadingPackages: boolean;
  packagesIsLoaded: boolean;
  licensePackagesError: boolean;
  allowPolling: boolean;
}

export const initialState: LicensesState = {
  ids: [],
  entities: {},
  accountLicenses: [],
  isLoadingLicenses: false,
  licensePackages: [],
  isLoadingPackages: false,
  packagesIsLoaded: false,
  licensePackagesError: false,
  allowPolling: true,
};
const adapter: EntityAdapter<License> = createEntityAdapter<License>({
  selectId: (license) => license.code,
});

export function licensesReducer(
  state: LicensesState = initialState,
  action: LicensesAction,
): LicensesState {
  switch (action.type) {
    case LicensesActionTypes.FetchAllLicenses: {
      return {
        ...state,
        isLoadingLicenses: true,
      };
    }
    case LicensesActionTypes.FetchAllLicensesError: {
      return {
        ...state,
        isLoadingLicenses: false,
      };
    }
    case LicensesActionTypes.FetchAllLicensesSuccess: {
      return adapter.setAll(
        action.payload.allLicenses.map((license) => {
          const al = action.payload.accountLicenses.find(
            (l) => l.licenseCode === license.code,
          );
          return {
            ...license,
            enabled: !!al,
            state: al ? al.state : null,
          };
        }),
        {
          ...state,
          isLoadingLicenses: false,
        },
      );
    }

    case LicensesActionTypes.UpdateLicenseInPackage: {
      const { license, licenseState } = action.payload;
      return adapter.updateOne(
        {
          id: license,
          changes: { state: licenseState, enabled: true },
        },
        state,
      );
    }

    case LicensesActionTypes.DisableLicenseNotInPackage: {
      const { license } = action.payload;
      return adapter.updateOne(
        {
          id: license,
          changes: { state: AccountState.Success, enabled: false },
        },
        state,
      );
    }

    case LicensesActionTypes.FetchLicensePackage: {
      return {
        ...state,
        licensePackagesError: false,
        isLoadingPackages: true,
      };
    }

    case LicensesActionTypes.FetchLicensePackageSuccess: {
      const { packages } = action.payload;
      return {
        ...state,
        licensePackages: packages,
        licensePackagesError: false,
        isLoadingPackages: false,
        packagesIsLoaded: true,
      };
    }

    case LicensesActionTypes.FetchLicensePackageError: {
      return {
        ...state,
        licensePackages: [],
        licensePackagesError: true,
        isLoadingPackages: false,
        packagesIsLoaded: false,
      };
    }

    case LicensesActionTypes.SetAllowPolling: {
      return {
        ...state,
        allowPolling: action.payload,
      };
    }
  }
  return state;
}

const { selectAll, selectEntities } = adapter.getSelectors();

export const getAllLicences = selectAll;
export const getAllEntities = selectEntities;
export const getLicensePackages = (state: LicensesState) =>
  state.licensePackages;
export const getIsLoadingPackages = (state: LicensesState) =>
  state.isLoadingPackages;
export const getPackagesIsLoaded = (state: LicensesState) =>
  state.packagesIsLoaded;
export const getLicensePackagesError = (state: LicensesState) =>
  state.licensePackagesError;
export const getAllowPolling = (state: LicensesState) => state.allowPolling;
export const getAllLicencesIsLoaded = (state: LicensesState) =>
  !state.isLoadingLicenses;
